import type { DropdownFilterItemContent } from '@carboncopyco/ui';
import { SearchDropdown, type SearchDropdownProps } from '../../SearchDropdown';
import { useUrlState } from '../../utils';
import type { UrlState } from '../types';

type SessionsBreakdownFilterProps = {
  type: keyof UrlState;
  options: DropdownFilterItemContent<string>[];
  capsuleWidth?: number;
  contentWidth?: number;
  query: string;
  setQuery: (query: string) => void;
} & Omit<
  SearchDropdownProps,
  'selectedItems' | 'onChange' | 'onClear' | 'searchableItems' | 'footer'
>;

export const SessionsBreakdownFilter = ({
  type,
  options,
  capsuleWidth = 208,
  contentWidth = 208,
  query,
  setQuery,
  ...rest
}: SessionsBreakdownFilterProps) => {
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const selectedOptions = urlState[type];

  const selectedItems = selectedOptions
    ? Object.fromEntries(
        selectedOptions.split(',').map((id) => {
          return [
            id,
            options.find((opportunity) => opportunity.id === id)?.label ?? '',
          ];
        })
      )
    : {};

  const onChange = (optionId) => {
    const optionsSet = new Set(
      selectedOptions?.split(',').map((option) => option.trim())
    );
    if (optionsSet.has(optionId)) {
      optionsSet.delete(optionId);
    } else {
      optionsSet.add(optionId);
    }
    setUrlState({
      ...urlState,
      [type]: Array.from(optionsSet.values()).join(','),
    });
  };

  return (
    <SearchDropdown
      {...rest}
      capsuleWidth={capsuleWidth}
      contentWidth={contentWidth}
      searchableItems={options}
      selectedItems={selectedItems}
      onChange={onChange}
      onClear={() => {
        setUrlState({ ...urlState, [type]: undefined });
        setQuery('');
      }}
      query={query}
      setQuery={setQuery}
    />
  );
};
